import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Form,
  Input,
  Button,
  Row,
  Col,
  Select,
  Card,
  Typography,
  message,
  InputNumber,
  AutoComplete,
  Alert,
  Tag,
  Tooltip,
  Collapse,
} from 'antd';
import { SaveOutlined, ArrowLeftOutlined, ThunderboltOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { Customer, Vehicle } from '../types';
import { customerApi, vehicleApi } from '../services/api';
import { useSessionStashedForm } from '../contexts/SessionGuardContext';
import { CarOutlined, TruckOutlined, ToolOutlined } from '@ant-design/icons';
import { getMakeSuggestions, getModelSuggestions } from '../data/vehicleData';
import { useAuth } from '../contexts/AuthContext';
import { getErrorMessage } from '../utils/apiError';

const { Title } = Typography;
const { TextArea } = Input;
const { Option } = Select;

// Helper function to get role-based path prefix
const getRolePrefix = (userRole?: string): string => {
  return userRole === 'customer' ? '/portal' :
         userRole === 'technician' ? '/technician' :
         userRole === 'advisor' ? '/advisor' :
         userRole === 'owner' ? '/owner' : '/admin';
};

interface VehicleFormProps {
  customerId?: number;
  propVehicleId?: number;
  onSuccess?: () => void;
  onCancel?: () => void;
  modal?: boolean;
}

// Labels for VIN-decoded spec fields displayed below the form
const DRIVE_TYPE_LABELS: Record<string, string> = {
  '4WD': '4WD / 4x4',
  'AWD': 'AWD',
  'FWD': 'FWD',
  '2WD': 'RWD / 2WD',
};

const FUEL_TYPE_COLORS: Record<string, string> = {
  diesel: 'orange',
  electric: 'green',
  hybrid: 'cyan',
  gasoline: 'default',
};

const VehicleForm: React.FC<VehicleFormProps> = ({ customerId, propVehicleId, onSuccess, onCancel, modal = false }) => {
  const [form] = Form.useForm();
  const assetType = (Form.useWatch('asset_type', form) || 'car') as 'car' | 'truck' | 'equipment';
  const isEquipment = assetType === 'equipment';
  const vinValue = Form.useWatch('vin', form) as string | undefined;
  const [loading, setLoading] = useState(false);
  const [vinDecoding, setVinDecoding] = useState(false);
  const [vehicle, setVehicle] = useState<Vehicle | null>(null);
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [selectedMake, setSelectedMake] = useState<string>('');
  const [vinSpecFields, setVinSpecFields] = useState<{
    drive_type: string | null;
    transmission_type: string | null;
    fuel_type: string | null;
    turbo: boolean;
  } | null>(null);
  const navigate = useNavigate();
  const { user } = useAuth();
  const rolePrefix = getRolePrefix(user?.role);
  const { id, vehicleId: urlVehicleId } = useParams<{ id: string; vehicleId: string }>();

  const currentVehicleId = propVehicleId || (urlVehicleId ? parseInt(urlVehicleId) : null);
  const isEditing = Boolean(currentVehicleId);
  const currentCustomerId = customerId || (id ? parseInt(id) : null);
  useSessionStashedForm(form, currentVehicleId ? `vehicle-${currentVehicleId}` : 'vehicle-new');

  const fetchCustomer = async (customerId: number) => {
    try {
      const response = await customerApi.getById(customerId);
      setCustomer(response.data);
    } catch (error) {
      message.error('Failed to fetch customer details');
    }
  };

  const fetchVehicle = async (vehicleId: number) => {
    try {
      const response = await vehicleApi.getById(vehicleId);
      setVehicle(response.data);
      setSelectedMake(response.data.make || '');
      form.setFieldsValue(response.data);
      // Restore any previously decoded spec fields
      if (response.data.drive_type || response.data.transmission_type || response.data.fuel_type) {
        setVinSpecFields({
          drive_type: response.data.drive_type ?? null,
          transmission_type: response.data.transmission_type ?? null,
          fuel_type: response.data.fuel_type ?? null,
          turbo: Boolean(response.data.turbo),
        });
      }
    } catch (error) {
      message.error('Failed to fetch vehicle details');
    }
  };

  useEffect(() => {
    if (currentCustomerId) {
      fetchCustomer(currentCustomerId);
    }
    if (isEditing && currentVehicleId) {
      fetchVehicle(currentVehicleId);
    }
  }, [currentCustomerId, isEditing, currentVehicleId]);

  const handleSubmit = async (values: any) => {
    if (!currentCustomerId) {
      message.error('Customer ID is required');
      return;
    }

    try {
      setLoading(true);

      const vehicleData = {
        ...values,
        customer_id: currentCustomerId,
      };

      if (isEditing && currentVehicleId) {
        await vehicleApi.update(currentVehicleId, vehicleData);
        message.success('Vehicle updated successfully');
      } else {
        await vehicleApi.create(vehicleData);
        message.success('Vehicle added successfully');
      }

      if (onSuccess) {
        onSuccess();
      } else {
        navigate(`${rolePrefix}/customers/${currentCustomerId}`);
      }
    } catch (error) {
      // The server says what is wrong — "VIN already exists" when the truck is
      // on another customer's record, for one. Swallowing it left the shop
      // staring at "Failed to save vehicle" with no way to tell what to change.
      message.error(getErrorMessage(error, 'Failed to save vehicle'));
    } finally {
      setLoading(false);
    }
  };

  /**
   * Decode the VIN using the NHTSA vPIC API via the backend.
   * Works for both new and existing vehicles.
   */
  const handleVinDecode = async () => {
    const vinValue = (form.getFieldValue('vin') || '').trim().toUpperCase();
    if (!vinValue || vinValue.length !== 17) {
      message.error('Please enter a valid 17-character VIN before decoding.');
      return;
    }
    // Normalize the field so the trimmed value is what gets submitted
    form.setFieldsValue({ vin: vinValue });

    setVinDecoding(true);
    try {
      let decoded: any;

      if (currentVehicleId) {
        // Existing vehicle: decode and update in DB
        const response = await vehicleApi.vinDecode(currentVehicleId);
        decoded = (response.data as any).decoded ?? (response.data as any).vin_decode;
        setVehicle((response.data as any).vehicle);
      } else {
        // New vehicle: standalone lookup (no DB record needed)
        const response = await vehicleApi.vinLookup(vinValue);
        decoded = response.data.decoded;
      }

      // Patch form with decoded values (only non-null)
      const patch: Record<string, any> = {};
      if (decoded.year) patch.year = decoded.year;
      if (decoded.make) patch.make = decoded.make;
      if (decoded.model) patch.model = decoded.model;
      if (decoded.engine_size) patch.engine_size = decoded.engine_size;
      if (decoded.body_class) patch.body_class = decoded.body_class;
      form.setFieldsValue(patch);
      if (decoded.make) setSelectedMake(decoded.make);

      // Display the decoded spec badges only when at least one spec is present,
      // otherwise the success Alert would render with a header but zero tags.
      const hasSpecs = Boolean(
        decoded.drive_type || decoded.transmission_type || decoded.fuel_type || decoded.turbo
      );
      setVinSpecFields(
        hasSpecs
          ? {
              drive_type: decoded.drive_type,
              transmission_type: decoded.transmission_type,
              fuel_type: decoded.fuel_type,
              turbo: decoded.turbo,
            }
          : null
      );

      message.success('VIN decoded — year, make, model & specs auto-filled.');
    } catch (err: any) {
      const detail = err?.response?.data?.error || err?.message || 'VIN decode failed';
      message.error(detail);
    } finally {
      setVinDecoding(false);
    }
  };

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: currentYear - 1900 + 1 }, (_, index) => currentYear - index);

  // Get autocomplete options
  const makeOptions = getMakeSuggestions().map(make => ({ value: make }));
  const modelOptions = selectedMake ? getModelSuggestions(selectedMake).map(model => ({ value: model })) : [];

  const commonColors = [
    'White', 'Black', 'Silver', 'Gray', 'Red', 'Blue', 'Brown', 'Green',
    'Orange', 'Yellow', 'Purple', 'Gold', 'Beige', 'Maroon'
  ];

  const handleBack = () => {
    if (currentCustomerId) {
      navigate(`${rolePrefix}/customers/${currentCustomerId}`);
    } else {
      navigate(`${rolePrefix}/customers`);
    }
  };

  return (
    <div style={modal ? {} : { padding: '24px', maxWidth: '1024px', margin: '0 auto' }}>
      {!modal && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '24px' }}>
          <Button
            type="text"
            icon={<ArrowLeftOutlined />}
            onClick={handleBack}
            style={{ color: '#6b7280' }}
          >
            Back
          </Button>
          <div>
            <Title level={2} style={{ marginBottom: 0 }}>
              {isEditing ? 'Edit Vehicle' : 'Add New Vehicle'}
            </Title>
            {customer && (
              <p style={{ color: '#6b7280' }}>
                for {customer.company_name || `${customer.first_name} ${customer.last_name}`}
              </p>
            )}
          </div>
        </div>
      )}

      <Card style={modal ? { boxShadow: 'none', border: 'none' } : { boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)' }} styles={modal ? { body: { padding: 0 } } : undefined}>
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          requiredMark={false}
          size="large"
          initialValues={{ asset_type: 'car' }}
        >
          {/* ── Asset Type selector ─────────────────────────────────────── */}
          <Form.Item
            name="asset_type"
            label="Asset Type"
            rules={[{ required: true, message: 'Please select an asset type' }]}
          >
            <Select
              placeholder="Select asset type"
              options={[
                { value: 'car', label: <span><CarOutlined /> Car</span> },
                { value: 'truck', label: <span><TruckOutlined /> Truck</span> },
                { value: 'equipment', label: <span><ToolOutlined /> Equipment</span> },
              ]}
            />
          </Form.Item>

          <Row gutter={[24, 16]}>
            <Col xs={24} md={8}>
              <Form.Item
                name="year"
                label="Year"
                rules={isEquipment ? [] : [{ required: true, message: 'Please select year' }]}
              >
                <Select
                  placeholder="Select year"
                  showSearch
                  optionFilterProp="children"
                >
                  {years.map(year => (
                    <Option key={year} value={year}>{year}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>

            <Col xs={24} md={8}>
              <Form.Item
                name="make"
                label={isEquipment ? 'Make (recommended)' : 'Make'}
                rules={isEquipment ? [] : [{ required: true, message: 'Please enter make' }]}
              >
                <AutoComplete
                  placeholder="Select or type make"
                  options={makeOptions}
                  filterOption={(inputValue, option) =>
                    option!.value.toLowerCase().indexOf(inputValue.toLowerCase()) !== -1
                  }
                  onChange={(value) => {
                    setSelectedMake(value);
                    // Clear model when make changes
                    form.setFieldsValue({ model: '' });
                  }}
                />
              </Form.Item>
            </Col>

            <Col xs={24} md={8}>
              <Form.Item
                name="model"
                label={isEquipment ? 'Model (recommended)' : 'Model'}
                rules={isEquipment ? [] : [{ required: true, message: 'Please enter model' }]}
              >
                <AutoComplete
                  placeholder={selectedMake ? "Select or type model" : "Select make first"}
                  options={modelOptions}
                  filterOption={(inputValue, option) =>
                    option!.value.toLowerCase().indexOf(inputValue.toLowerCase()) !== -1
                  }
                  disabled={!selectedMake}
                />
              </Form.Item>
            </Col>
          </Row>

          {/* ── Equipment fields ─────────────────────────────────────────── */}
          {isEquipment && (
            <>
              <Row gutter={[24, 16]}>
                <Col xs={24} md={12}>
                  <Form.Item name="serial_number" label="Serial Number">
                    <Input
                      placeholder="Enter serial number"
                      style={{ textTransform: 'uppercase' }}
                      onChange={(e) => {
                        const value = e.target.value.toUpperCase();
                        form.setFieldsValue({ serial_number: value });
                      }}
                    />
                  </Form.Item>
                </Col>

                <Col xs={24} md={6}>
                  <Form.Item name="hours" label="Hours">
                    <InputNumber
                      placeholder="Run hours"
                      style={{ width: '100%' }}
                      formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                      parser={(value) => {
                        const parsed = parseInt(value?.replace(/\$\s?|(,*)/g, '') || '0');
                        const result = isNaN(parsed) ? 0 : Math.max(parsed, 0);
                        return result as any;
                      }}
                      min={0}
                    />
                  </Form.Item>
                </Col>

                <Col xs={24} md={6}>
                  <Form.Item name="engine_size" label="Engine Size">
                    <Input placeholder="e.g., 2.4L, V6 diesel" />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item name="asset_description" label="Description">
                <TextArea
                  rows={3}
                  placeholder="Describe this equipment (type, attachments, identifying details)..."
                  showCount
                  maxLength={500}
                />
              </Form.Item>
            </>
          )}

          {/* ── Car / Truck VIN + mileage ───────────────────────────────── */}
          {!isEquipment && (
          <Row gutter={[24, 16]}>
            <Col xs={24} md={12}>
              <Form.Item
                name="vin"
                label={
                  <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    VIN <span style={{ color: '#9ca3af', fontWeight: 400 }}>(optional)</span>
                    <Tooltip title="Auto-fill year, make, model, engine & specs from NHTSA database">
                      <Button
                        type="link"
                        size="small"
                        icon={<ThunderboltOutlined />}
                        loading={vinDecoding}
                        onClick={handleVinDecode}
                        style={{ padding: '0 4px', height: 'auto', fontSize: 12, color: '#6366f1' }}
                      >
                        {vinDecoding ? 'Decoding...' : 'Decode VIN'}
                      </Button>
                    </Tooltip>
                  </span>
                }
                rules={[
                  { len: 17, message: 'VIN must be exactly 17 characters' }
                ]}
              >
                <Input
                  placeholder="Enter 17-character VIN"
                  maxLength={17}
                  style={{ textTransform: 'uppercase' }}
                  onChange={(e) => {
                    const value = e.target.value.toUpperCase();
                    form.setFieldsValue({ vin: value });
                  }}
                />
              </Form.Item>
            </Col>

            <Col xs={24} md={6}>
              <Form.Item
                name="current_mileage"
                label="Current Mileage"
                rules={[{ required: true, message: 'Please enter current mileage' }]}
              >
                <InputNumber
                  placeholder="Enter mileage"
                  style={{ width: '100%' }}
                  formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  parser={(value) => {
                    const parsed = parseInt(value?.replace(/\$\s?|(,*)/g, '') || '0');
                    const result = isNaN(parsed) ? 0 : Math.min(Math.max(parsed, 0), 999999);
                    return result as any;
                  }}
                  min={0}
                  max={999999}
                />
              </Form.Item>
            </Col>

            <Col xs={24} md={6}>
              <Form.Item
                name="engine_size"
                label="Engine Size"
              >
                <Input placeholder="e.g., 2.4L, V6, 1.8L Turbo" />
              </Form.Item>
            </Col>

            {/* Drive type, editable by hand.
                It was written only by the VIN decoder and shown as a read-only
                tag, so a vehicle with no VIN could never record it. ALLDATA
                publishes 2WD and 4WD as SEPARATE vehicles with different parts —
                the 4WD front hub takes a halfshaft nut the 2WD does not — so a
                lookup on a truck with this blank has to stop and ask rather than
                guess. This is the field that answers it. */}
            <Col xs={24} md={6}>
              <Form.Item
                name="drive_type"
                label="Drive Type"
                tooltip="ALLDATA quotes 2WD and 4WD as different vehicles. Set this so a parts lookup picks the right one."
              >
                <Select
                  allowClear
                  placeholder="2WD / 4WD"
                  options={Object.entries(DRIVE_TYPE_LABELS).map(([value, label]) => ({
                    value,
                    label,
                  }))}
                />
              </Form.Item>
            </Col>
          </Row>
          )}

          {/* ── VIN-optional soft warning (car/truck only) ──────────────── */}
          {!isEquipment && !(vinValue && vinValue.trim()) && (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
              message="Add a VIN for the most accurate results (VIN decode + recall lookups)."
            />
          )}

          {/* ── VIN Decoded Spec Badges ─────────────────────────────────── */}
          {vinSpecFields && (
            <Alert
              type="success"
              icon={<CheckCircleOutlined />}
              showIcon
              style={{ marginBottom: 16 }}
              message={
                <span style={{ fontWeight: 600 }}>Vehicle specs decoded from VIN</span>
              }
              description={
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 6 }}>
                  {vinSpecFields.drive_type && (
                    <Tag color={vinSpecFields.drive_type === '4WD' || vinSpecFields.drive_type === 'AWD' ? 'blue' : 'default'}>
                      {DRIVE_TYPE_LABELS[vinSpecFields.drive_type] ?? vinSpecFields.drive_type}
                    </Tag>
                  )}
                  {vinSpecFields.transmission_type && (
                    <Tag color="purple" style={{ textTransform: 'capitalize' }}>
                      {vinSpecFields.transmission_type.toUpperCase()}
                    </Tag>
                  )}
                  {vinSpecFields.fuel_type && (
                    <Tag color={FUEL_TYPE_COLORS[vinSpecFields.fuel_type] ?? 'default'} style={{ textTransform: 'capitalize' }}>
                      {vinSpecFields.fuel_type.charAt(0).toUpperCase() + vinSpecFields.fuel_type.slice(1)}
                    </Tag>
                  )}
                  {vinSpecFields.turbo && (
                    <Tag color="volcano" icon={<ThunderboltOutlined />}>Turbocharged</Tag>
                  )}
                  <span style={{ fontSize: 12, color: '#6b7280', alignSelf: 'center' }}>
                    These specs are used to automatically calculate interference add-ons when generating labor estimates.
                  </span>
                </div>
              }
            />
          )}

          {/* ── New vehicle hint ─── */}
          {!isEditing && !isEquipment && (
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
              message="Tip: Enter the VIN and click Decode VIN to automatically fill in year, make, model, drivetrain, fuel type, and transmission."
            />
          )}

          {/* ── More Details (optional, collapsible) ─── */}
          <Collapse ghost items={[{
            key: 'more',
            label: 'More Details (optional)',
            children: (
              <>
                <Row gutter={[24, 16]}>
                  <Col xs={24} md={8}>
                    <Form.Item
                      name="license_plate"
                      label="License Plate"
                    >
                      <Input
                        placeholder="Enter license plate"
                        style={{ textTransform: 'uppercase' }}
                        onChange={(e) => {
                          const value = e.target.value.toUpperCase();
                          form.setFieldsValue({ license_plate: value });
                        }}
                      />
                    </Form.Item>
                  </Col>

                  <Col xs={24} md={8}>
                    <Form.Item
                      name="color"
                      label="Color"
                    >
                      <Select
                        placeholder="Select or enter color"
                        showSearch
                        optionFilterProp="children"
                      >
                        {commonColors.map(color => (
                          <Option key={color} value={color}>{color}</Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>

                  <Col xs={24} md={8}>
                    <Form.Item
                      name="last_service_mileage"
                      label="Last Service Mileage"
                    >
                      <InputNumber
                        placeholder="Enter last service mileage"
                        style={{ width: '100%' }}
                        formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                        parser={(value) => {
                          const parsed = parseInt(value?.replace(/\$\s?|(,*)/g, '') || '0');
                          const result = isNaN(parsed) ? 0 : Math.min(Math.max(parsed, 0), 999999);
                          return result as any;
                        }}
                        min={0}
                        max={999999}
                      />
                    </Form.Item>
                  </Col>
                </Row>

                <Form.Item name="notes" label="Notes">
                  <TextArea
                    rows={4}
                    placeholder="Enter any additional notes about this vehicle..."
                    showCount
                    maxLength={500}
                  />
                </Form.Item>
              </>
            )
          }]} />

          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '12px', paddingTop: '16px', borderTop: '1px solid #f3f4f6' }}>
            <Button
              type="default"
              onClick={modal ? onCancel : handleBack}
              size="large"
            >
              Cancel
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              icon={<SaveOutlined />}
              size="large"
              style={{ fontWeight: '500' }}
            >
              {isEditing ? 'Update Vehicle' : 'Add Vehicle'}
            </Button>
          </div>
        </Form>
      </Card>
    </div>
  );
};

export default VehicleForm;
